import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, UserX, UserCheck, Trash2, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '', password: '', company_name: '', contact_name: '', phone: '', role: 'pro'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/admin/users`, formData);
      toast.success('Utilisateur créé');
      setIsDialogOpen(false);
      setFormData({ email: '', password: '', company_name: '', contact_name: '', phone: '', role: 'pro' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const toggleUserStatus = async (userId) => {
    try {
      const response = await axios.put(`${API_URL}/admin/users/${userId}/toggle`);
      toast.success(response.data.message);
      fetchUsers();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`);
      toast.success('Utilisateur supprimé');
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Gestion des utilisateurs</h1>
          <p className="text-white/50 mt-1">{users.length} utilisateur(s)</p>
        </div>
        <button onClick={() => setIsDialogOpen(true)} className="btn-primary flex items-center gap-2" data-testid="add-user-btn">
          <Plus className="w-4 h-4" />
          Créer un compte
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-white/50 text-xs uppercase tracking-wider py-4 px-4">Entreprise</th>
                <th className="text-left text-white/50 text-xs uppercase tracking-wider py-4 px-4">Contact</th>
                <th className="text-left text-white/50 text-xs uppercase tracking-wider py-4 px-4">Email</th>
                <th className="text-left text-white/50 text-xs uppercase tracking-wider py-4 px-4">Rôle</th>
                <th className="text-left text-white/50 text-xs uppercase tracking-wider py-4 px-4">Statut</th>
                <th className="text-right text-white/50 text-xs uppercase tracking-wider py-4 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <motion.tr
                  key={user.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.03 }}
                  className="border-b border-white/5 hover:bg-white/5"
                >
                  <td className="py-4 px-4">
                    <p className="text-white font-medium">{user.company_name}</p>
                  </td>
                  <td className="py-4 px-4">
                    <p className="text-white/70">{user.contact_name}</p>
                    <p className="text-white/40 text-xs">{user.phone}</p>
                  </td>
                  <td className="py-4 px-4 text-white/70">{user.email}</td>
                  <td className="py-4 px-4">
                    <span className={`px-3 py-1 text-xs uppercase tracking-wider ${
                      user.role === 'admin' 
                        ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30' 
                        : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`px-3 py-1 text-xs uppercase tracking-wider ${
                      user.is_active 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                        : 'bg-red-500/20 text-red-400 border border-red-500/30'
                    }`}>
                      {user.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center justify-end gap-2">
                      <button 
                        onClick={() => toggleUserStatus(user.id)} 
                        className={`p-2 ${user.is_active ? 'text-white/40 hover:text-red-400' : 'text-white/40 hover:text-green-400'}`}
                        title={user.is_active ? 'Désactiver' : 'Activer'}
                      >
                        {user.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                      </button>
                      <button 
                        onClick={() => deleteUser(user.id)} 
                        className="p-2 text-white/40 hover:text-red-400"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create User Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-[#1C1C1C] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Créer un utilisateur</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-white/60 text-sm mb-2 block">Email *</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
                data-testid="user-email-input"
              />
            </div>
            <div>
              <label className="text-white/60 text-sm mb-2 block">Mot de passe *</label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
                data-testid="user-password-input"
              />
            </div>
            <div>
              <label className="text-white/60 text-sm mb-2 block">Nom de l'entreprise *</label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                required
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
              />
            </div>
            <div>
              <label className="text-white/60 text-sm mb-2 block">Nom du contact *</label>
              <Input
                value={formData.contact_name}
                onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                required
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
              />
            </div>
            <div>
              <label className="text-white/60 text-sm mb-2 block">Téléphone *</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                required
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
              />
            </div>
            <div>
              <label className="text-white/60 text-sm mb-2 block">Rôle</label>
              <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1C1C] border-white/10">
                  <SelectItem value="pro" className="text-white hover:bg-white/10">Professionnel</SelectItem>
                  <SelectItem value="admin" className="text-white hover:bg-white/10">Administrateur</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-4">
              <button type="button" onClick={() => setIsDialogOpen(false)} className="btn-secondary flex-1">
                Annuler
              </button>
              <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-2">
                <Save className="w-4 h-4" />
                Créer
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
